import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { navLinks, WHATSAPP_URL } from "@/data/content";

function LogoMark({ light }) {
  return (
    <a href="#inicio" data-testid="nav-logo" className="flex items-center gap-3">
      <span className="relative flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-royal to-brand-dark shadow-lg">
        <svg viewBox="0 0 24 24" className="h-6 w-6 text-white" fill="currentColor" aria-hidden="true">
          <path d="M21.5 4.5c-1.2.4-2.3.4-3.3.1-1-.9-2.3-1.4-3.7-1.4-2.9 0-5.2 2.4-5.2 5.2 0 .4 0 .8.1 1.2C5 9.9 2.7 6.9 2 4.3c-.5.8-.7 1.7-.7 2.7 0 1.8.9 3.4 2.3 4.4-.9 0-1.7-.3-2.4-.7v.1c0 2.5 1.8 4.6 4.2 5.1-.4.1-.9.2-1.4.2-.3 0-.7 0-1-.1.7 2.1 2.6 3.6 4.9 3.7-1.8 1.4-4 2.2-6.5 2.2-.4 0-.8 0-1.2-.1 2.3 1.5 5.1 2.4 8 2.4 9.6 0 14.9-8 14.9-14.9v-.7c.8-.5 1.4-1.2 1.9-2z" opacity="0" />
          <path d="M12 3c-2.5 0-4.6 1.8-5.1 4.2-.2.9.3 1.6 1.2 1.6 2 0 3.9 1.1 3.9 3.2 0 1.4-1 2.6-2.4 2.9-2.9.7-5.1 2.9-5.6 5.8-.1.6.4 1.3 1.1 1.3h13.8c.7 0 1.2-.6 1.1-1.3C19.5 11.5 16.1 3 12 3z" />
        </svg>
        <span className="absolute -bottom-1 -right-1 rounded-md bg-brand-orange px-1.5 py-0.5 text-[9px] font-extrabold text-white shadow">
          40
        </span>
      </span>
      <span className="leading-tight">
        <span className={`block font-display text-sm sm:text-base font-extrabold tracking-tight ${light ? "text-white" : "text-slate-900"}`}>
          Rosangela da Saúde
        </span>
        <span className={`block text-[10px] sm:text-xs font-bold uppercase tracking-widest ${light ? "text-brand-light" : "text-brand-royal"}`}>
          Deputada Federal · PSB
        </span>
      </span>
    </a>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const light = !scrolled && !open;

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl shadow-lg shadow-slate-900/5 border-b border-slate-200/70" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-8 lg:px-12">
        <LogoMark light={light} />

        <nav className="hidden items-center gap-6 lg:flex" aria-label="Navegação principal">
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testid}
              className={`text-sm font-semibold transition-colors duration-200 hover:text-brand-orange ${
                light ? "text-white/90" : "text-slate-700"
              }`}
            >
              {l.label}
            </a>
          ))}
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta-whatsapp"
            className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-brand-orange/30 transition-transform duration-200 hover:-translate-y-0.5"
          >
            <MessageCircle className="h-4 w-4" />
            Fale no WhatsApp
          </a>
        </nav>

        <button
          type="button"
          data-testid="nav-mobile-toggle"
          aria-label={open ? "Fechar menu" : "Abrir menu"}
          onClick={() => setOpen((v) => !v)}
          className={`lg:hidden rounded-xl p-2 transition-colors ${light ? "text-white" : "text-slate-900"}`}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden bg-white/95 backdrop-blur-xl border-b border-slate-200 lg:hidden"
            aria-label="Menu móvel"
          >
            <div className="space-y-1 px-6 py-6">
              {navLinks.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  data-testid={`mobile-${l.testid}`}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  onClick={() => setOpen(false)}
                  className="block rounded-xl px-4 py-3 text-base font-bold text-slate-800 hover:bg-brand-royal/5 hover:text-brand-royal"
                >
                  {l.label}
                </motion.a>
              ))}
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-nav-cta-whatsapp"
                className="mt-3 flex items-center justify-center gap-2 rounded-full bg-brand-orange px-5 py-3.5 text-sm font-bold text-white shadow-lg"
              >
                <MessageCircle className="h-4 w-4" />
                Fale no WhatsApp
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
